<template>
    <div class="position-relative">
        <div class="d-flex position-absolute" style="width: calc(100% - 210px); top: 10px; z-index: 100; right: 10px;">

            <input
                type="text"
                ref="mapInput"
                placeholder="Enter an address..."
                v-model="displayLocation"
                class="form-control me-2"
                style="height: 40px; flex: 1;"
            >

            <button
                class="btn btn-primary me-2"
                style="width: 130px;"
                data-bs-toggle="modal"
                :data-bs-target="'#' + (mapFor + '-address-lookup')"
            >
                Lookup
            </button>

            <button
                v-if="jkLookupEnabled"
                class="btn btn-warning"
                style="width: 130px;"
                data-bs-toggle="modal"
                :data-bs-target="'#' + jkLookupId">
                JK Lookup
            </button>

        </div>

        <div ref="mapElement" style="height: 400px;" ></div>

        <AddressLookup
            :for-map="mapFor"
            :customer="selectedCustomer"
            :identifier="mapFor + '-address-lookup'"
            @update:address="updateAddress"
        />

        <div class="modal" :id="jkLookupId" tabindex="-1" v-if="jkLookupEnabled">
            <div class="modal-dialog modal-dialog-centered" style="max-width: 900px;">
                <div class="modal-content">

                    <div class="modal-header">
                        <h5 class="modal-title text-center">
                            JK Address Lookup
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div class="modal-body bg-light" style="">
                        <div class="block block-rounded mb-0">
                            <div class="block-content block-content-full">

                                <AddressLookupJk
                                    :for-map="this.mapFor"
                                    :customer="selectedCustomer"
                                    @update:address="updateAddress"
                                />

                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                            Close
                        </button>
                    </div>

                </div>
            </div>
        </div>

    </div>
</template>

<script>

import AddressLookup from '@/components/movements/jobs/create/AddressLookup.vue';
import AddressLookupJk from '@/components/movements/jobs/create/AddressLookupJk.vue';
import { Loader } from "@googlemaps/js-api-loader";
import { useId } from "vue";

const UK_FALLBACK_CENTER = {
    lat: 54.5,
    lng: -3.0,
};

    export default {

        props: {
            mapFor: {
                type: String,
                default: () => `google-map-${useId()}`,
                required: false,
            },
            location : {
                type: String,
                required: true,
            },
            selectedCustomer: {
                type: Object,
                required: false,
                default: null,
            },
            jkLookupEnabled: {
                type: Boolean,
                default: false,
            },
        },

        components: {
            AddressLookup,
            AddressLookupJk,
        },

        data() {
            return {
                internalLocation: this.location,
                displayLocation: this.location,
                internalLatitude: null,
                internalLongitude: null,
                internalSelectedCustomer: this.selectedCustomer,
                marker: null,
                map: null,
                locale: '',
            }
        },

        methods: {

            async loadLibraries() {
                const [ { Map }, { AdvancedMarkerElement }, { Geocoder } ] = await Promise.all([
                    google.maps.importLibrary("maps"),
                    google.maps.importLibrary("marker"),
                    google.maps.importLibrary("geocoding"),
                ]);

                return { Map, AdvancedMarkerElement, Geocoder };
            },

            /**
             * This takes an address string and returns the latitude and longitude
             * For some reason during testing, passing in a lat and lng does not accurately show the location
             * This is apparently because of JS floating point handling
             */
            async initialise() {

                let { Map, AdvancedMarkerElement, Geocoder } = await this.loadLibraries();

                try {

                    if (!this.internalLocation || this.internalLocation === '') {
                        if (this.$refs.mapElement === null) {
                            console.error("Map element is null, cannot initialise map");
                            return;
                        }

                        this.map = new Map(this.$refs.mapElement, {
                            mapId: this.mapFor + '-map',
                            center: UK_FALLBACK_CENTER,
                            zoom: 6,
                            fullscreenControl: false,
                            scrollwheel: false,
                        });

                        this.marker = null;
                        return;
                    }

                    new Geocoder().geocode({'address': this.internalLocation}, (results, status) => {
                        if (status === 'OK' && results[0]) {

                            let position = results[0].geometry.location;

                            if (position === null || position === undefined) {
                                console.error("Position is null, cannot initialise map");
                                return;
                            }

                            if (this.$refs.mapElement === null) {
                                console.error("Map element is null, cannot initialise map");
                                return;
                            }

                            this.map = new Map(this.$refs.mapElement, {
                                mapId: this.mapFor + '-map',
                                center: position,
                                zoom: 14,
                                fullscreenControl: false,
                                scrollwheel: false,
                            });

                            this.marker = new AdvancedMarkerElement({
                                map: this.map,
                                position: position,
                                draggable: true,
                            });

                            this.internalLatitude = position.lat();
                            this.internalLongitude = position.lng();

                            var locale = [];
                            results[0].address_components.forEach(function (component) {
                                switch (component.types[0]) {
                                    case 'locality':
                                    case 'postal_town':
                                    case 'administrative_area_level_2':
                                        // sometimes locality and postal_town are the same value so try not to make duplicates
                                        if (locale.indexOf(component.long_name) < 0) {
                                            locale.push(component.long_name);
                                        }
                                        break;
                                }
                            });
                            this.locale = locale.join(', ');

                            if (
                                this.internalLatitude &&
                                this.internalLongitude &&
                                this.internalLocation
                            ) {

                                this.$emit('location-changed', {
                                    latitude: this.internalLatitude,
                                    longitude: this.internalLongitude,
                                    location: this.internalLocation,
                                    locale: this.locale
                                });

                            } else {
                                console.error("Latitude or Longitude is null, cannot emit location changed event");
                            }

                            this.applyAliases();

                        }
                    });

                } catch (e) {
                    console.error("Error initialising map", e);
                }

            },

            // This fires when the address is changed via the input field
            async addEventListeners() {

                const {Autocomplete} = await google.maps.importLibrary("places")

                /*
                 * Fields dictates what gets returned in the place object from the API
                 * We are charged for each field returned fyi
                 */
                let autocomplete = new Autocomplete(this.$refs.mapInput, {
                    fields: [
                        'address_component',
                        'formatted_address',
                        'geometry'
                    ],
                });

                autocomplete.addListener('place_changed', () => {

                    let place = autocomplete.getPlace();

                    if (!place) {
                        window.alert("No details available for input: '" + place.name + "'");
                        return;
                    }

                    this.internalLatitude = place.geometry.location.lat();
                    this.internalLongitude = place.geometry.location.lng();
                    this.internalLocation = place.formatted_address;
                    this.displayLocation = place.formatted_address; // keep display in sync
                    this.map.setCenter(place.geometry.location);

                    this.marker = new google.maps.marker.AdvancedMarkerElement({
                        map: this.map,
                        position: place.geometry.location,
                        draggable: true,
                        title: "Delivery",
                    });

                    if (
                        this.internalLatitude &&
                        this.internalLongitude &&
                        this.internalLocation
                    ) {

                        this.$emit('location-changed', {
                            latitude: this.internalLatitude,
                            longitude: this.internalLongitude,
                            location: this.internalLocation,
                            locale: this.locale
                        });

                    } else {
                        console.error("Location, Latitude or Longitude is null, cannot emit location changed event");
                    }

                });

                this.$refs.mapInput.addEventListener('keydown', function(e) {
                    if (e.code === "Enter") {
                        e.preventDefault();
                    }
                });

            },

            // This changes then the address is selected from the modal
            updateAddress(values) {

                if ( !values || !values.address || !values.lat || !values.lng ) {
                    console.error("Invalid address values", values);
                    return;
                }

                this.internalLocation = values.address;

                this.internalLatitude = values.lat;
                this.internalLongitude = values.lng;

                this.$emit('location-changed', {
                    latitude: this.internalLatitude,
                    longitude: this.internalLongitude,
                    location: this.internalLocation,
                    locale: this.locale
                });

            },

            async boot() {
                await this.initialise();
                await this.addEventListeners();
            },

            applyAliases() {

                const jk_office = '13 witham point wavell dr';
                const jk_storage = 'fosseway business park a46 fosse ln';

                const processedDisplayLocation = this.displayLocation.toLowerCase().replace(",", '').trim();

                if ( processedDisplayLocation.includes(jk_storage)) {
                    this.displayLocation = 'JK Storage';
                } else if ( processedDisplayLocation.includes(jk_office)) {
                    this.displayLocation = 'JK Office';
                }

            },


        },

        async mounted() {

            const loader = new Loader({
                apiKey: 'AIzaSyD_tfgEUWxTtVczG5euJnkbeZ9DryHp4F4',
                version: "weekly",
                libraries: ["places", "geocoding", "maps", "marker"],
            });

            // This is used literally as a way to detect Google is loaded. We don't actually need to load a library here
            this.$nextTick(async () => {
                await loader.importLibrary('places');
                await this.boot();

                if (
                    this.internalLatitude === null ||
                    this.internalLatitude === undefined ||
                    this.internalLongitude === null ||
                    this.internalLongitude === undefined ||
                    this.internalLocation === null ||
                    this.internalLocation === undefined
                ) {
                    console.error("Latitude or Longitude is null, cannot emit location changed event");
                    return;
                }

                this.$emit('location-changed', {
                    latitude: this.internalLatitude,
                    longitude: this.internalLongitude,
                    location: this.internalLocation,
                    locale: this.locale
                });
            });

        },

        watch: {
            location: {
                handler: function (val) {

                    this.internalLocation = val;
                    this.displayLocation = val;

                    this.boot();

                    this.applyAliases();

                },
                deep: true
            },
            selectedCustomer: {
                handler: function (val) {
                    this.internalSelectedCustomer = val;
                },
                deep: true
            }
        },

        computed: {
            jkLookupId() {
                return this.mapFor + '-jk-lookup';
            }
        }

    }
</script>
